import { useGetSavedSlidesQuery } from "@api/slides-library";
import { useEffect, useMemo } from "react";
import type { TSavedSlide, TSlidesFolder } from "@/shared/types";
import {
  useGetCurrentUserQuery,
  useGetSlidesFoldersQuery,
} from "@/shared/services";
import { useSlidesLibraryStoreShallow } from "../../../shared/store";

const collectSubtreeIds = (
  rootId: string,
  folders: TSlidesFolder[],
): Set<string> => {
  const childrenByParent = new Map<string, TSlidesFolder[]>();
  for (const folder of folders) {
    const parent = folder.parentId ?? "";
    if (!childrenByParent.has(parent)) childrenByParent.set(parent, []);
    childrenByParent.get(parent)!.push(folder);
  }
  const ids = new Set<string>([rootId]);
  const queue: string[] = [rootId];
  while (queue.length) {
    const id = queue.shift()!;
    for (const child of childrenByParent.get(id) ?? []) {
      ids.add(child._id);
      queue.push(child._id);
    }
  }
  return ids;
};

export const useSearchSavedSlides = () => {
  const { data: me } = useGetCurrentUserQuery();
  const { search, type, folderId, setIsSearching } =
    useSlidesLibraryStoreShallow((s) => ({
      search: s.search,
      type: s.type,
      folderId: s.folderId,
      setIsSearching: s.setIsSearching,
    }));

  const isSavedType = type === "user-slides" || type === "team-slides";
  const trimmed = search.trim();

  const { data: slides, isFetching } = useGetSavedSlidesQuery(
    { userId: me?.oktaId ?? "", folderId: "", search: trimmed },
    { skip: !me?.oktaId || !isSavedType || !trimmed },
  );

  const { data: folders } = useGetSlidesFoldersQuery();

  useEffect(() => {
    if (!search) {
      setIsSearching(false);
      return;
    }
    setIsSearching(isFetching);
  }, [search, isFetching, setIsSearching]);

  const allowedFolderIds = useMemo<Set<string>>(() => {
    const root =
      folderId === "user-slides" || folderId === "team-slides"
        ? folderId
        : folderId || (type as string);
    if (!folders) return new Set<string>([root, ""]);
    const ids = collectSubtreeIds(root, folders as TSlidesFolder[]);
    if (root === "user-slides" || root === "team-slides") ids.add("");
    return ids;
  }, [folderId, folders, type]);

  const filtered = useMemo<TSavedSlide[]>(() => {
    if (!slides) return [];
    const query = trimmed.toLowerCase();
    return (slides as TSavedSlide[]).filter((slide) => {
      if (slide.archived) return false;
      if (!allowedFolderIds.has(slide.folderId ?? "")) return false;
      if (!query) return true;
      return slide.title.toLowerCase().includes(query);
    });
  }, [slides, trimmed, allowedFolderIds]);

  return { loading: isFetching, search, data: filtered };
};
